import { ShotData, ShotComparisonData } from '../types';
import { calculateRealisticDistance, calculateOptimalDistance, ensureLogicalDistances } from './unifiedPhysics';

/**
 * Calculate optimal shot parameters based on cricket physics
 */
function calculateOptimalShot(shot: ShotData): ShotData {
  // Optimal launch angle for maximum distance in cricket (15-25 degrees)
  const optimalLaunchAngle = 20;
  
  // Speeds are already in m/s according to types
  const ballSpeedMs = shot.ballSpeed; // Already in m/s
  const batSpeedMs = shot.batSpeed; // Already in m/s
  
  // Calculate optimal exit velocity using cricket ball physics
  // For a perfect shot with optimal bat speed and sweet spot contact
  // Optimal bat speed for power: 120-140 km/h (33-39 m/s)
  const cor = 0.55; // Maximum COR for perfect sweet spot contact
  
  // Calculate optimal exit velocity for perfect contact using realistic physics
  // Use coefficient of restitution formula: v2 = v1 + e(u1 - u2) where u1, u2 are approach velocities
  const optimalExitVelocityMs = (1 + cor) * batSpeedMs + ballSpeedMs * (1 - cor);
  
  // Apply realistic constraints for cricket
  const MAX_EXIT_VELOCITY = 55; // m/s (198 km/h - world-class maximum)
  const actualExitVelocityMs = shot.exitVelocity; // Already in m/s
  
  // Calculate realistic improvement potential
  let finalOptimalVelocityMs: number;
  if (actualExitVelocityMs >= 50) {
    // Already near maximum, minimal improvement possible
    finalOptimalVelocityMs = Math.min(MAX_EXIT_VELOCITY, actualExitVelocityMs + 2);
  } else {
    // Room for improvement, but cap at realistic maximum
    const calculatedOptimal = Math.min(optimalExitVelocityMs, actualExitVelocityMs * 1.1);
    finalOptimalVelocityMs = Math.min(MAX_EXIT_VELOCITY, calculatedOptimal);
  }
  
  // Keep in m/s for consistency with data storage
  const optimalExitVelocity = finalOptimalVelocityMs;
  
  // Optimal spin rate for stability
  const optimalSpinRate = 1500;
  
  // Optimal impact location (sweet spot)
  const optimalImpactX = 0;
  const optimalImpactY = 0.15; // 15cm from center is typical sweet spot
  
  // Use unified physics for consistent distance calculation
  const actualDistance = calculateRealisticDistance({
    exitVelocity: shot.exitVelocity,
    launchAngle: shot.launchAngle,
    spinRate: shot.spinRate,
    quality: shot.quality
  });
  
  const calculatedOptimalDistance = calculateOptimalDistance({
    exitVelocity: finalOptimalVelocityMs,
    launchAngle: optimalLaunchAngle,
    spinRate: optimalSpinRate,
    quality: 'excellent'
  });
  
  // Ensure logical consistency (optimal >= actual)
  const { actual: _consistentActual, optimal: optimalDistance } = ensureLogicalDistances(
    actualDistance,
    calculatedOptimalDistance
  );
  
  return {
    ...shot,
    launchAngle: optimalLaunchAngle,
    exitVelocity: optimalExitVelocity,
    spinRate: optimalSpinRate,
    impactLocationX: optimalImpactX,
    impactLocationY: optimalImpactY,
    distance: optimalDistance,
    quality: 'excellent',
    timing: 'excellent',
    footwork: 'excellent',
    balance: 'excellent',
    powerTransfer: 0.95
  };
}

/**
 * Calculate trajectory data for a shot
 */
function calculateTrajectory(shot: ShotData) {
  const g = 9.81;
  const angleRad = (shot.launchAngle * Math.PI) / 180;
  
  // Exit velocity is already in m/s according to types
  const exitVelocityMs = shot.exitVelocity;
  const vx = exitVelocityMs * Math.cos(angleRad);
  const vy = exitVelocityMs * Math.sin(angleRad);
  
  // Calculate max height
  const maxHeight = (Math.pow(vy, 2)) / (2 * g);
  
  // Calculate flight time
  const flightTime = (2 * vy) / g;
  
  // Calculate landing angle
  const landingVy = -vy; // Same magnitude, opposite direction
  const landingAngle = Math.atan2(landingVy, vx) * 180 / Math.PI;
  
  // Calculate realistic distance instead of using hardcoded value
  const realisticDistance = calculateRealisticDistance({
    exitVelocity: shot.exitVelocity,
    launchAngle: shot.launchAngle,
    spinRate: shot.spinRate,
    quality: shot.quality
  });

  return {
    points: [], // Would need full trajectory calculation
    maxHeight,
    totalDistance: realisticDistance,
    flightTime,
    landingAngle: Math.abs(landingAngle)
  };
}

/**
 * Generate recommendations based on shot comparison
 */
function generateRecommendations(actual: ShotData, optimal: ShotData): string[] {
  const recommendations: string[] = [];
  
  // Launch angle recommendations
  if (Math.abs(actual.launchAngle - optimal.launchAngle) > 5) {
    if (actual.launchAngle < optimal.launchAngle) {
      recommendations.push('Increase launch angle by hitting slightly under the ball');
    } else {
      recommendations.push('Decrease launch angle by hitting through the line of the ball');
    }
  }
  
  // Bat speed recommendations
  if (actual.batSpeed < optimal.batSpeed * 0.9) {
    recommendations.push('Generate more bat speed through improved hip rotation');
  }
  
  // Impact location recommendations
  if (actual.sweetSpotDistance > 0.05) {
    recommendations.push('Focus on hitting the sweet spot for better power transfer');
  }
  
  // Timing recommendations
  if (actual.timing !== 'excellent') {
    recommendations.push('Work on timing drills to improve ball contact');
  }
  
  // Power transfer recommendations
  if (actual.powerTransfer < 0.8) {
    recommendations.push('Improve power transfer through better weight transfer');
  }
  
  return recommendations;
}

/**
 * Generate drill suggestions based on areas for improvement
 */
function generateDrillSuggestions(actual: ShotData, optimal: ShotData): string[] {
  const drills: string[] = [];
  
  if (actual.timing !== 'excellent') {
    drills.push('Throwdown practice with varying speeds');
  }
  
  if (actual.footwork !== 'excellent') {
    drills.push('Footwork ladder drills for better positioning');
  }
  
  if (actual.balance !== 'excellent') {
    drills.push('Balance board exercises for stability');
  }
  
  if (actual.powerTransfer < 0.8) {
    drills.push('Medicine ball rotational throws for core strength');
  }
  
  if (actual.batSpeed < optimal.batSpeed * 0.9) {
    drills.push('Weighted bat swings for increased bat speed');
  }
  
  return drills;
}

/**
 * Calculate comprehensive shot comparison data
 */
export function calculateShotComparison(shot: ShotData): ShotComparisonData {
  const optimal = calculateOptimalShot(shot);
  const actualTrajectory = calculateTrajectory(shot);
  const optimalTrajectory = calculateTrajectory(optimal);
  
  const optimization = {
    actual: {
      launchAngle: shot.launchAngle,
      exitVelocity: shot.exitVelocity,
      horizontalAngle: shot.horizontalAngle,
      spinRate: shot.spinRate,
      impactLocation: {
        x: shot.impactLocationX,
        y: shot.impactLocationY
      }
    },
    optimal: {
      launchAngle: optimal.launchAngle,
      exitVelocity: optimal.exitVelocity,
      horizontalAngle: optimal.horizontalAngle,
      spinRate: optimal.spinRate,
      impactLocation: {
        x: optimal.impactLocationX,
        y: optimal.impactLocationY
      }
    },
    improvements: {
      launchAngleDelta: optimal.launchAngle - shot.launchAngle,
      velocityDelta: optimal.exitVelocity - shot.exitVelocity,
      horizontalAngleDelta: optimal.horizontalAngle - shot.horizontalAngle,
      spinRateDelta: optimal.spinRate - shot.spinRate,
      impactLocationDelta: {
        x: optimal.impactLocationX - shot.impactLocationX,
        y: optimal.impactLocationY - shot.impactLocationY
      }
    },
    potentialDistanceGain: optimal.distance - shot.distance,
    recommendations: generateRecommendations(shot, optimal),
    drillSuggestions: generateDrillSuggestions(shot, optimal)
  };
  
  return {
    actual: shot,
    optimal,
    optimization,
    actualTrajectory,
    optimalTrajectory
  };
}